import { Stack, SimpleGrid, Text, Box, Table, Badge } from '@mantine/core'
import {
  IconActivity,
  IconRocket,
  IconAlertTriangle,
  IconClock,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { SmallBox, DataCard, DashboardChart, Callout } from '@/components/ui'
import { gitlabApi, type TeamMetrics } from '@/api/gitlab'

/**
 * Team Tab Component (TASK-1260)
 *
 * Shows team metrics: activity, deployment frequency, incidents, lead time
 *
 * TASK-10996: a resposta pode vir com `degraded: true` — a leitura no GitLab
 * estourou o deadline e os campos vem ZERADOS. Esta era a pior das cinco rotas
 * de dashboard GitLab: as outras devolvem lista vazia, esta devolve zeros
 * calculados, que na tela sao identicos a "o time nao entregou nada".
 */
export function TeamTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id

  const metrics = useQuery<TeamMetrics>({
    queryKey: ['gitlab-metrics', tenantId],
    queryFn: () => gitlabApi.getTeamMetrics(tenantId!),
    enabled: !!tenantId,
    staleTime: 300_000,
  })

  const data = metrics.data
  const degradado = data?.degraded === true
  /** Numero medido, ou '—' quando a leitura falhou (zero de falha nao e zero). */
  const medido = (valor: number | undefined) => (degradado || valor == null ? '—' : valor)

  return (
    <Stack gap="xl">
      {degradado && (
        <Callout variant="warning" title="Leitura do GitLab incompleta">
          A consulta ao GitLab não respondeu dentro do prazo, então as métricas deste período
          vieram <strong>zeradas por falta de leitura</strong> — não são medição do time.
        </Callout>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <SmallBox
          value={medido(data?.activity)}
          label="Atividade (pipelines/mes)"
          icon={<IconActivity size={40} />}
          color="primary"
          loading={metrics.isLoading}
          info="Total de pipelines executadas pelo time no período. Indica o nível de atividade de desenvolvimento e entregas."
        />
        <SmallBox
          value={medido(data?.deployment_frequency)}
          label="Deploys no Período"
          icon={<IconRocket size={40} />}
          color="success"
          loading={metrics.isLoading}
          info="Número de deploys realizados no período. Alta frequência indica maturidade em práticas de entrega contínua (CI/CD)."
        />
        <SmallBox
          value={medido(data?.velocity)}
          label="MRs Mergeadas"
          icon={<IconActivity size={40} />}
          color="info"
          loading={metrics.isLoading}
          info="Quantidade de Merge Requests concluídas no período. Reflete a velocidade de entrega e colaboração do time."
        />
        <SmallBox
          value={!degradado && data?.lead_time_hours ? `${data.lead_time_hours}h` : '—'}
          label="Lead Time Medio"
          icon={<IconClock size={40} />}
          color={!degradado && data?.lead_time_hours && data.lead_time_hours > 48 ? 'warning' : 'success'}
          loading={metrics.isLoading}
          info="Tempo médio desde o primeiro commit até o merge de uma MR. Preocupe-se quando ultrapassar 48h, pois pode indicar gargalos no processo de revisão."
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <DataCard flush title="Frequência de Deploy" accent="success">
          <Box p="md">
            {metrics.isLoading ? (
              <Text ta="center" c="dimmed" py="xl">Carregando...</Text>
            ) : degradado ? (
              <Text ta="center" c="dimmed" py="xl">
                Sem leitura do GitLab — o gráfico não é medição
              </Text>
            ) : (
              // TASK-11016: estas quatro "semanas" NAO existem no dado — sao o
              // total do periodo dividido por 4/3/2/1. Nao e escopo da 10996.
              <DashboardChart
                title=""
                type="bar"
                data={[
                  { name: 'Sem 1', deploys: Math.max(1, Math.floor((data?.deployment_frequency ?? 0) / 4)) },
                  { name: 'Sem 2', deploys: Math.max(1, Math.floor((data?.deployment_frequency ?? 0) / 3)) },
                  { name: 'Sem 3', deploys: Math.max(1, Math.floor((data?.deployment_frequency ?? 0) / 2)) },
                  { name: 'Sem 4', deploys: data?.deployment_frequency ?? 0 },
                ]}
                height={200}
              />
            )}
          </Box>
        </DataCard>

        <DataCard flush title="Métricas do Período" accent="info">
          <Table striped>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Pipelines executadas</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light" color={degradado ? 'gray' : undefined}>{medido(data?.activity)}</Badge></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>MRs mergeadas</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light" color={degradado ? 'gray' : 'green'}>{medido(data?.velocity)}</Badge></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Deploys</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light" color={degradado ? 'gray' : 'teal'}>{medido(data?.deployment_frequency)}</Badge></Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Lead Time</Text></Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={degradado ? 'gray' : 'orange'}>
                    {degradado ? '—' : `${data?.lead_time_hours ?? 0}h`}
                  </Badge>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm" fw={500}>Período</Text></Table.Td>
                <Table.Td><Badge size="sm" variant="light" color="gray">{data?.period ?? 'month'}</Badge></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </DataCard>
      </SimpleGrid>
    </Stack>
  )
}
